<script>
  import { onDestroy, onMount, tick } from "svelte"
  import { derived } from "svelte/store"
  import { addMonths, format, parse } from "date-fns"
  import { createForm } from "felte";
  import { validator } from "@felte/validator-zod";
  import { z } from "zod";
  import { useQuery } from "@sveltestack/svelte-query"
  import { getNotificationsContext } from "svelte-notifications";
  import DialogBase2 from "../../../components/dialog/DialogBase2.svelte";
  import DialogTitle from "../../../components/dialog/DialogTitle.svelte";
  import DialogContent from "../../../components/dialog/DialogContent.svelte";
  import ProjectSettingsDatetimeInput
    from "../../../components/project/adminPage/parts/ProjectSettingsDatetimeInput.svelte";
  import ProjectSettingsIntervalSelect
    from "../../../components/project/adminPage/parts/ProjectSettingsIntervalSelect.svelte";
  import InputContainer from "../../parts/inputs/InputContainer.svelte";
  import Select from "../../parts/inputs/Select.svelte";
  import ProjectSettingsWeekdayMultipleSelection
    from "../../../components/project/adminPage/parts/ProjectSettingsWeekdayMultipleSelection.svelte";
  import Stack from "../../../components/layout/Stack.svelte";
  import DialogSplitter from "../../../components/dialog/DialogSplitter.svelte";
  import DialogBottomControls from "../../../components/dialog/DialogBottomControls.svelte";
  import DialogBottomControlButton from "../../../components/dialog/DialogBottomControlButton.svelte";
  import { Cron } from "croner"
  import ProjectSettingsTextInput from "../../../components/project/adminPage/parts/ProjectSettingsTextInput.svelte";
  import HelpTooltip from "../../../components/tooltip/HelpTooltip.svelte";
  import Checkbox2 from "../../../components/input/Checkbox2.svelte";
  import { getAutoMpsProjectContext } from "../../AutoMpsProjectContext.js";
  import { translate } from "../../../i18n/i18next.js";

  /**
   * Close handler
   * @type {() => {}}
   */
  export let onClose

  export let initialValues = undefined;

  const ctx = getAutoMpsProjectContext()
  const {addNotification} = getNotificationsContext()

  let dialog = null
  onMount(async() => {
    await tick()
    dialog?.showModal()
  })
  onDestroy(() => {
    dialog?.close()
  })

  const executionSchedulesQueryResult = useQuery(`/api/auto-mps/projects/${$ctx.projectId}/settings/execution-schedules`)

  const schema = z.object({
    schedulingParameterName: z.string(),
    startDate: z.string(),
    startTime: z.string(),
    disableEndDate: z.boolean(),
    endDate: z.string(),
    endTime: z.string(),
    interval: z.union([
      z.literal("weekly"),
      z.literal("daily"),
      z.literal("hourly"),
    ]),
    intervalNum: z.coerce.number().optional(),
    selectedWeekdays: z.record(z.string(), z.boolean())
  })

  async function handleSubmit(values) {
    console.log("submission", values);
    const executionSchedulesObj = constructExecutionSchedulesObj(values)
    const response = await fetch(
      `/api/auto-mps/projects/${$ctx.projectId}/settings/execution-schedules`,
      {
        method: "POST",
        body: JSON.stringify({
          ...executionSchedulesObj.data,
          originalValues: JSON.stringify(values),
        }),
        headers: {
          "Content-Type": "application/json",
        }
      }
    )
    if (response.ok) {
      addNotification({
        text: translate("frontend:generic.notifications.saved"),
        type: "success",
        removeAfter: 4000,
        position: "top-right",
      })
      await $executionSchedulesQueryResult.refetch()
      onClose()
    } else {
      addNotification({
        text: translate("frontend:generic.notifications.saveFailed"),
        type: "error",
        removeAfter: 4000,
        position: "top-right",
      })
    }
  }

  const today = new Date()
  const {form, errors, data, createSubmitHandler, isValid} = createForm({
    onSubmit: handleSubmit,
    initialValues: initialValues ?? {
      schedulingParameterName: "main",
      startDate: format(today, "yyyy-MM-dd"),
      startTime: "00:00",
      disableEndDate: false,
      endDate: format(addMonths(today, 1), "yyyy-MM-dd"),
      endTime: "00:00",
      interval: "weekly",
      intervalNum: 1,
      selectedWeekdays: {
        "sunday": today.getDay() === 0,
        "monday": today.getDay() === 1,
        "tuesday": today.getDay() === 2,
        "wednesday": today.getDay() === 3,
        "thursday": today.getDay() === 4,
        "friday": today.getDay() === 5,
        "saturday": today.getDay() === 6,
      },
    },
    extend: validator({schema})
  })

  function constructExecutionSchedulesObj(data) {
    const cronString = constructCronString(data)
    const startDate = parse(data.startDate + " " + data.startTime, "yyyy-MM-dd HH:mm", new Date())
    const endDate = data.disableEndDate ? null : parse(data.endDate + " " + data.endTime, "yyyy-MM-dd HH:mm", new Date())
    return {
      cron: new Cron(cronString, {
        startAt: startDate.toISOString().slice(0, -5),
        stopAt: endDate?.toISOString().slice(0, -5),
      }),
      data: {
        cronString,
        startAt: startDate.toISOString().slice(0, -5),
        stopAt: endDate?.toISOString().slice(0, -5) ?? null,
        schedulingParameter: data.schedulingParameterName,
        paused: false,
      }
    }
  }

  const interval = derived([data, isValid], ([data, isValid]) => {
    if (!isValid) return null
    const obj = constructExecutionSchedulesObj(data)
    return obj.cron
  })

  $: if (Object.values($errors).filter(val => val).length > 0) console.log("errors", $errors)
  const submitHandler = createSubmitHandler()

  function constructCronString(values) {
    const base = {
      second: "0",
      minute: parseInt(values.startTime.split(":")[1], 10).toString(10),
      hour: parseInt(values.startTime.split(":")[0], 10).toString(10),
      day: "*",
      month: "*",
      weekday: "*"
    }
    if (values.interval === "weekly") {
      const weekdayString = Object.entries(values.selectedWeekdays)
        .filter(entry => entry[1])
        .map(entry => {
          switch (entry[0]) {
            case "monday":
              return 1
            case "tuesday":
              return 2
            case "wednesday":
              return 3
            case "thursday":
              return 4
            case "friday":
              return 5
            case "saturday":
              return 6
            case "sunday":
              return 7
          }
        })
        .join(",")
      if (weekdayString) {
        base.weekday = weekdayString
      }
    } else if (values.interval === "hourly") {
      base.hour = `*/${values.intervalNum ?? 1}`
    } else if (values.interval === "daily") {
      base.day = `*/${values.intervalNum ?? 1}`
    }
    return `${base.second} ${base.minute} ${base.hour} ${base.day} ${base.month} ${base.weekday}`
  }
</script>

<DialogBase2 bind:dialog={dialog} width="900px">
  <DialogTitle>定期的な実行を計画する</DialogTitle>
  <DialogSplitter />
  <DialogContent>
    <form class="sub-section" use:form={form}>
      <Stack direction="vertical" gap="1rem">
        <ProjectSettingsTextInput
          name="schedulingParameterName"
        >
          実行する計画パラメタ名
          <HelpTooltip title="計画パラメタ名">
            実行するAruファイル内に存在する計画パラメタを指定する必要があります。
          </HelpTooltip>
        </ProjectSettingsTextInput>
        <Stack direction="horizontal" gap="2rem" alignItems="center">
          <ProjectSettingsDatetimeInput
            datePartName="startDate"
            timePartName="startTime"
          >
            期間開始日時
            <HelpTooltip title="期間開始日時">
              定期実行が有効となる期間の開始日時です。設定時点の日時より後である必要があります。
            </HelpTooltip>
          </ProjectSettingsDatetimeInput>
          <span>~</span>
          <ProjectSettingsDatetimeInput
            datePartName="endDate"
            timePartName="endTime"
            disabled={$data.disableEndDate}
          >
            期間終了日時
            <HelpTooltip title="期間終了日時">
              定期実行が有効となる期間の終了日時です。期間開始日時より後である必要があります。終了日時を無効にした場合、開始日時以降常に有効となります。
            </HelpTooltip>
          </ProjectSettingsDatetimeInput>
          <div style="align-self: end">
            <Checkbox2 label="無効にする" name="disableEndDate" />
          </div>
        </Stack>
        <Stack direction="horizontal" gap="2rem" alignItems="center">
          <ProjectSettingsIntervalSelect
            label="実行頻度"
            name="interval"
          />
          {#if $data.interval === "daily"}
            <InputContainer>
              <span slot="label">繰り返し間隔</span>
              <Select name="intervalNum">
                {#each [1, 2, 3, 4, 5, 6, 7] as num}
                  <option value={num}>{num}日ごと</option>
                {/each}
              </Select>
            </InputContainer>
          {/if}
          {#if $data.interval === "hourly"}
            <InputContainer>
              <span slot="label">繰り返し間隔</span>
              <Select name="intervalNum">
                {#each [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as num}
                  <option value={num}>{num}時間ごと</option>
                {/each}
              </Select>
            </InputContainer>
          {/if}
        </Stack>
        {#if $data.interval === "weekly"}
          <ProjectSettingsWeekdayMultipleSelection
            label="実行する曜日"
            name="selectedWeekdays"
            bind:selectedWeekdays={$data.selectedWeekdays}
          />
        {/if}
      </Stack>
    </form>
    <Stack direction="vertical" gap=".5rem">
      <div>実行スケジュール</div>
      <div>直近の10回分の予定を表示しています。</div>
      {#if $interval}
        <Stack direction="vertical" gap=".3rem" style="padding-left: 1rem">
          {#each $interval.nextRuns(10).map(date => format(date, "yyyy/M/d HH:mm (eee)")) as dateString, i}
            <div>{i+1}: {dateString}</div>
          {/each}
        </Stack>
      {:else}
        <div>
          予定がありません
        </div>
      {/if}
    </Stack>
  </DialogContent>
  <DialogSplitter/>
  <DialogBottomControls>
    <DialogBottomControlButton type="primary" onClick={() => submitHandler()}>
      保存する
    </DialogBottomControlButton>
    <DialogBottomControlButton type="secondary" onClick={() => onClose()}>
      キャンセル
    </DialogBottomControlButton>
  </DialogBottomControls>
</DialogBase2>

<style>
  .sub-section {
    display: flex;
    flex-direction: column;
    gap: .8rem;
    padding: .5rem 0
  }
</style>